import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import {
  Icon,
  Link,
} from '@resy/ui-kitchen';
import {
  Slider, Slide, CarouselContext, Dot, ButtonBack, ButtonNext,
} from 'pure-react-carousel';
import { handleMktAnalytics } from '../../AmexMarketingUtils/AmexUtils';
import { CTA_EVENT_NAME } from '../../AmexMarketingUtils/constants';
import AmexExperientialBannerCarouselSlide from './AmexExperientialBannerCarouselSlide';
import './AmexExperientialBannerCarousel.scss';

const AmexExperientialBannerCarousel = (
  {
    matchMedia,
    baseRequestUrl,
    bannerConfig: {
      skip_link: skipLink,
      skip_link_aria: skipLinkAria,
      sr_title: srTitle,
      sr_info: srInfo,
      sr_controls_aria: srControlsAria,
    },
    sortedCarouselData,
    Analytics,
    scrollTo,
  }
) => {
  const items = sortedCarouselData.map((o, i) => i);
  const carouselContext = useContext(CarouselContext);
  const [currentSlide, setCurrentSlide] = useState(carouselContext.state.currentSlide);
  const nextHeading = document.querySelectorAll('h2')[0].id;

  const handleAnalyticsClick = (slideNumber) => {
    const extraParam = {
      action_name: 'Banner - Slide Click',
      item_name: sortedCarouselData[slideNumber]?.header,
    };
    handleMktAnalytics(Analytics.event, CTA_EVENT_NAME, extraParam);
  };

  useEffect(() => {
    const onChange = () => {
      setCurrentSlide(carouselContext.state.currentSlide);
      handleAnalyticsClick(carouselContext.state.currentSlide);
    };
    carouselContext.subscribe(onChange);
    return () => carouselContext.unsubscribe(onChange);
  }, [carouselContext]);

  const renderButtons = () => (
    <>
      <ButtonBack
        aria-label="Previous slide"
        className="AmexExperientialBannerCarousel__controls--buttons left"
        disabled={null}
        aria-disabled={currentSlide === 0}
      >
        <span aria-hidden="true"><Icon name="caret" /></span>
      </ButtonBack>
      <ButtonNext
        aria-label="Next slide"
        className="AmexExperientialBannerCarousel__controls--buttons right"
        disabled={null}
        aria-disabled={currentSlide === items.length - 1}
      >
        <span aria-hidden="true"><Icon name="caret" /></span>
      </ButtonNext>
    </>
  );

  return (
    <div className="AmexExperientialBannerCarousel">
      <div
        className="AmexExperientialBannerCarousel__wrapper"
        aria-label={srTitle}
        role="region"
        aria-roledescription="carousel"
      >
        {skipLink && (
          <Link
            href={`#${nextHeading}`}
            className="AmexExperientialBannerCarousel__skiplink"
            aria-label={skipLinkAria}
            onClick={() => {
              scrollTo('#experiences');
            }}
          >
            {skipLink}
          </Link>
        )}
        <p className="AmexExperientialBannerCarousel__sr-text sr-only">
          {srInfo}
        </p>
        <div
          className="AmexExperientialBannerCarousel__controls"
          role="group"
          aria-label={srControlsAria}
        >
          <div className="container">
            <ul>
              {sortedCarouselData.map((o, i) => (
                <li
                  key={o.orderId}
                  className="AmexExperientialBannerCarousel__controls--slidepicker"
                >
                  <Dot
                    aria-label={`Slide ${i + 1} of ${items.length} - ${o.header}`}
                    className={`navbutton ${i === currentSlide ? 'current' : ''}`}
                    aria-current={i === currentSlide}
                    disabled={false}
                    slide={i}
                    onClick={() => setCurrentSlide(i)}
                  />
                </li>
              ))}
            </ul>
            {renderButtons()}
          </div>
        </div>
        <div
          className="AmexExperientialBannerCarousel__slider"
        >
          <Slider
            role={undefined}
            trayProps={{ role: 'listbox', 'aria-label': 'slider' }}
          >
            {sortedCarouselData.map((slide, i) => {
              const props = {
                key: slide.header,
                index: i,
                currentSlide: i === currentSlide,
                image: slide.image,
                retina_image: slide.retina_image,
                header: slide.header,
                copy: slide.copy,
                cta: slide.cta,
                baseRequestUrl,
                Analytics,
                matchMedia,
              };
              return (
                <Slide
                  index={i}
                  tabIndex={-1}
                  id={`slide${i}`}
                  key={slide.header}
                  aria-roledescription="Slide"
                  aria-labelledby={`slide_heading${i}`}
                  aria-hidden={i !== currentSlide}
                >
                  <AmexExperientialBannerCarouselSlide
                    {...props}
                  />
                </Slide>
              );
            })}
          </Slider>
        </div>
      </div>
    </div>
  );
};

AmexExperientialBannerCarousel.propTypes = {
  baseRequestUrl: PropTypes.string.isRequired,
  bannerConfig: PropTypes.shape({
    skip_link: PropTypes.string,
    skip_link_aria: PropTypes.string,
    sr_title: PropTypes.string,
    sr_info: PropTypes.string,
    sr_controls_aria: PropTypes.string,
  }).isRequired,
  matchMedia: PropTypes.shape({
    xs: PropTypes.bool,
    sm: PropTypes.bool,
    md: PropTypes.bool,
    lg: PropTypes.bool,
    xl: PropTypes.bool,
  }).isRequired,
  Analytics: PropTypes.shape({
    event: PropTypes.func,
  }).isRequired,
  scrollTo: PropTypes.func.isRequired,
  sortedCarouselData: PropTypes.arrayOf(PropTypes.shape({
    header: PropTypes.string,
  })).isRequired,
};

export default AmexExperientialBannerCarousel;
